import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingUp, Crown, Swords, TriangleAlert } from 'lucide-react';
import axios from 'axios';

// Custom Tooltip Component - positions itself outside the pie center
const StatsTooltip = ({ active, payload, data, totalWins, coordinate }) => {
  if (active && payload && payload.length && coordinate) {
    const maxValue = Math.max(...data.map(d => d.value));
    const winners = data.filter(d => d.value === maxValue && maxValue > 0);
    const isTied = winners.length > 1;
    const isWinner = payload[0].value === maxValue && maxValue > 0;
    const elementData = data.find(d => d.name === payload[0].name);
    
    // Calculate position to push tooltip outward from center
    // Pie center is at 50% of container (roughly 200px for 400px container)
    const pieCenter = { x: 200, y: 200 };
    const dx = coordinate.x - pieCenter.x;
    const dy = coordinate.y - pieCenter.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Normalize and push outward by 80px from the cursor position
    const pushDistance = 80;
    const offsetX = distance > 0 ? (dx / distance) * pushDistance : 0;
    const offsetY = distance > 0 ? (dy / distance) * pushDistance : 0;
    
    const style = {
      transform: `translate(${offsetX}px, ${offsetY}px)`,
    };
    
    return (
      <div className="stats-tooltip" style={style}>
        <p className="tooltip-label flex items-center gap-2" style={{ color: elementData?.color }}>
          {payload[0].name}
          {isWinner && !isTied && <Crown size={16} className="text-yellow-400" style={{ fill: '#fbbf24' }} />}
          {isWinner && isTied && <Swords size={16} className="text-yellow-400" />}
        </p>
        <p className="tooltip-value" style={{ color: elementData?.color }}>{payload[0].value}% Win Rate</p>
        <p className="tooltip-wins">{elementData?.wins || 0} / {totalWins} Wins</p>
        {elementData?.champion && (
          <p className="tooltip-champion" style={{ color: elementData?.color, borderColor: elementData?.color }}>
            Champion: {elementData.champion.id} ({elementData.champion.type})
          </p>
        )}
      </div>
    );
  }
  return null;
};

const StatsSection = () => {
  const [animationComplete, setAnimationComplete] = useState(false);
  const [data, setData] = useState([
    { name: 'Fire', value: 0, wins: 0, color: '#ff4500', champion: null },
    { name: 'Water', value: 0, wins: 0, color: '#00bfff', champion: null },
    { name: 'Earth', value: 0, wins: 0, color: '#8b4513', champion: null },
    { name: 'Air', value: 0, wins: 0, color: '#00ff00', champion: null }
  ]);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [totalWins, setTotalWins] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [hasReceivedData, setHasReceivedData] = useState(false); // Track if we've ever gotten data

  // Intersection Observer to detect when section is visible
  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 } // Trigger when 10% of section is visible
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Fetch live win data from external API via backend proxy
  const fetchWinData = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/proxy/getwins`, {}, {
        timeout: 20000 // 20 second timeout
      });

      // Check for error response from backend
      if (response.data && response.data.error) {
        setIsConnected(false); // Mark as disconnected on error
        return; // Exit early if there's an error
      }

      // Process response data
      if (response.data && response.data.Fire !== undefined) {
        // Get win counts from backend response
        const fireWins = response.data.Fire_wins || 0;
        const waterWins = response.data.Water_wins || 0;
        const earthWins = response.data.Earth_wins || 0;
        const airWins = response.data.Air_wins || 0;
        const total = response.data._total_wins || (fireWins + waterWins + earthWins + airWins);
        
        setTotalWins(total);
        setIsConnected(true); // Mark as connected on success
        setHasReceivedData(true); // Mark that we've received data at least once
        
        const newData = [
          { 
            name: 'Fire', 
            value: response.data.Fire || 0,
            wins: fireWins,
            color: '#ff4500',
            champion: response.data.Fire_champion || null
          },
          { 
            name: 'Water', 
            value: response.data.Water || 0,
            wins: waterWins,
            color: '#00bfff',
            champion: response.data.Water_champion || null
          },
          { 
            name: 'Earth', 
            value: response.data.Earth || 0,
            wins: earthWins,
            color: '#8b4513',
            champion: response.data.Earth_champion || null
          },
          { 
            name: 'Air', 
            value: response.data.Air || 0,
            wins: airWins,
            color: '#00ff00',
            champion: response.data.Air_champion || null
          }
        ];
        setData(newData);
      }
    } catch (error) {
      setIsConnected(false); // Mark as disconnected on error - indicator will turn grey
      // Keep using existing data if fetch fails
    }
  };

  // Poll API every 15 seconds when section is visible
  useEffect(() => {
    let interval;

    if (isVisible) {
      // Fetch immediately when section becomes visible
      fetchWinData();
      
      // Then fetch every 15 seconds
      interval = setInterval(() => {
        fetchWinData();
      }, 15000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isVisible]); // fetchWinData is stable as it only uses setState functions

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Memoized tooltip renderer to pass data context
  const renderTooltip = useCallback((props) => (
    <StatsTooltip {...props} data={data} totalWins={totalWins} />
  ), [data, totalWins]);

  return (
    <section id="stats" ref={sectionRef} className="stats-section section-padding bg-card-bg">
      <div className="container-custom">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TrendingUp className="text-brand-primary" size={32} />
            <h2 className="heading-2">Arena Dominance</h2>
          </div>
          <p className="body-medium text-text-secondary max-w-2xl mx-auto">
            Live statistics from thousands of battles across all arenas
            {isVisible && (
              <span className={`ml-2 ${isConnected ? 'live-indicator-connected' : 'live-indicator-disconnected'}`}>
                ● LIVE
              </span>
            )}
          </p>
        </div>

        <div className="stats-wrapper">
          <div className="chart-container">
            {hasReceivedData ? (
              <div className="chart-wrapper">
                <ResponsiveContainer width="100%" height={500}>
                  <PieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={180}
                      innerRadius={110}
                      fill="#8884d8"
                      dataKey="value"
                      animationBegin={0}
                      animationDuration={1500}
                      animationEasing="ease-out"
                    >
                      {data.map((entry) => (
                        <Cell key={`cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={renderTooltip}
                      offset={25}
                      wrapperStyle={{ 
                        zIndex: 100,
                        pointerEvents: 'none'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                
                {/* Center content showing winner or tie */}
                {(() => {
                  const maxValue = Math.max(...data.map(d => d.value));
                  const winners = data.filter(d => d.value === maxValue && maxValue > 0);
                  
                  if (winners.length === 0) return null;
                  
                  // Check for tie (multiple winners - 2 or more)
                  if (winners.length > 1) {
                    const numTied = winners.length;
                    
                    // Special case: 2-way tie - show names above and below swords
                    if (numTied === 2) {
                      return (
                        <div className="chart-center-content">
                          <div className="center-element-name" style={{ color: winners[0].color }}>
                            {winners[0].name}
                          </div>
                          <Swords size={36} className="center-swords" style={{ color: '#fbbf24' }} />
                          <div className="center-element-name" style={{ color: winners[1].color }}>
                            {winners[1].name}
                          </div>
                          <div className="center-win-count">
                            Tied at {winners[0].wins} / {totalWins}
                          </div>
                        </div>
                      );
                    }
                    
                    // 3+ way tie: Arrange names in a circular formation in the center
                    const radius = 80; // Distance from center
                    
                    return (
                      <div className="chart-center-content tie-circular-layout">
                        {/* Element names arranged in a circle */}
                        {winners.map((winner, index) => {
                          // Evenly distribute around the circle, starting from top (-90 degrees)
                          const angle = (index * (360 / numTied)) - 90;
                          const angleRad = (angle * Math.PI) / 180;
                          const x = Math.cos(angleRad) * radius;
                          const y = Math.sin(angleRad) * radius;
                          
                          return (
                            <div 
                              key={winner.name}
                              className="circular-element-name"
                              style={{
                                transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                                color: winner.color
                              }}
                            >
                              {winner.name}
                            </div>
                          );
                        })}
                        
                        {/* Center content: swords and tie text */}
                        <div className="tie-center-content">
                          <Swords size={36} className="center-swords" style={{ color: '#fbbf24' }} />
                          <div className="center-win-count">
                            {numTied}-Way Tie
                          </div>
                          <div className="center-win-count-small">
                            {winners[0].wins} / {totalWins}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // Single winner
                  const winner = winners[0];
                  return (
                    <div className="chart-center-content">
                      <Crown size={48} className="center-crown" style={{ fill: '#fbbf24' }} />
                      <div className="center-element-name" style={{ color: winner.color }}>{winner.name}</div>
                      <div className="center-percentage" style={{ color: winner.color }}>{winner.value}%</div>
                      <div className="center-win-count">{winner.wins} / {totalWins}</div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="chart-error-state">
                <TriangleAlert size={200} className="hazard-icon" />
                <p className="error-message">Server is Currently Not Responding</p>
              </div>
            )}
          </div>

          {/* Only show stats info when we have data */}
          {hasReceivedData && (
            <div className="stats-info">
              <div className="stats-cards">
              {[...data].sort((a, b) => b.value - a.value).map((element) => {
                const maxValue = Math.max(...data.map(d => d.value));
                const winners = data.filter(d => d.value === maxValue && maxValue > 0);
                const isTied = winners.length > 1;
                const isWinner = element.value === maxValue && maxValue > 0;
                
                return (
                  <div key={element.name} className="stat-card" style={{ '--stat-color': element.color }}>
                    <div className="stat-header">
                      <div className="flex items-center gap-2">
                        <h3 className="heading-5" style={{ color: element.color }}>{element.name}</h3>
                        {isWinner && !isTied && (
                          <Crown 
                            size={24} 
                            className="text-yellow-400 crown-icon" 
                            style={{ fill: '#fbbf24' }} 
                          />
                        )}
                        {isWinner && isTied && (
                          <Swords 
                            size={24} 
                            className="text-yellow-400" 
                          />
                        )}
                      </div>
                      <div className="stat-indicator" style={{ backgroundColor: element.color }}></div>
                    </div>
                    <div className="stat-value">
                      <span className="stat-percentage" style={{ color: element.color }}>{element.value}%</span>
                      <span className="stat-label">Win Rate</span>
                    </div>
                    <div className="stat-wins-display">
                      {element.wins} / {totalWins} Wins
                    </div>
                    {element.champion && (
                      <div className="stat-champion" style={{ color: element.color, borderColor: element.color }}>
                        Champion: {element.champion.id}
                      </div>
                    )}
                    <div className="stat-bar">
                      <div 
                        className="stat-bar-fill" 
                        style={{ 
                          width: animationComplete ? `${element.value * 3}%` : '0%',
                          backgroundColor: element.color 
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="stats-note">
              <p className="body-small text-text-secondary">
                <strong>Note:</strong> Statistics based on ranked arena battles. Win rates are continuously updated and may fluctuate based on player skill, team composition, and meta changes.
              </p>
            </div>
          </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
